// Sendeablauf eines Kanals: was läuft, was als Nächstes kommt, mit voraussichtlichen Zeiten.
// Titel verschieben, herausnehmen, einfügen und Moderationen (Voice-Tracks) an eine Stelle setzen.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { Icon, errorText } from "../components/streaming-ui";
import { Callout, EmptyState, EqBars, Page, PageHeader, Skeleton } from "../components/ui";
import { MessageInbox, RequestInbox } from "../components/listener-ui";
import type { LibraryItem, PlanEntry } from "../types";

// className = Farbe des Punkts im Art-Chip (dieselben Farben wie in den Stundenuhren).
const KIND_STYLE: Record<string, { label: string; className: string }> = {
  music: { label: "Musik", className: "bg-blue-500" },
  jingle: { label: "Jingle", className: "bg-amber-500" },
  ad: { label: "Werbung", className: "bg-red-500" },
  news: { label: "Nachrichten", className: "bg-emerald-500" },
  speech: { label: "Wort", className: "bg-violet-500" },
  voicetrack: { label: "Moderation", className: "bg-fuchsia-500" },
  live: { label: "Live", className: "bg-red-600" },
};

/** Kleine Symbolknöpfe in einer Zeile des Ablaufs. */
const ICON_BTN = "btn btn-ghost btn-sm !w-7 !px-0";

const time = (iso: string): string => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const mmss = (ms: number | null): string => {
  if (!ms) return "";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export function RundownPage(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const canEdit = user?.role !== "librarian";
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const canModerate = isAdmin || user?.role === "moderator";
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getPlan>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [insertAfter, setInsertAfter] = useState<string | null | undefined>(undefined);
  // Welcher Eintrag gerade seinen Moderationstext offen hat (nur einer zur Zeit).
  const [textFor, setTextFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.getPlan(slug, 3));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (fn: () => Promise<unknown>, success?: string): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (success) setNotice(success);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
      await load();
    }
  };

  if (!data) {
    return (
      <Page>
        {error ? <Callout tone="danger">{error}</Callout> : <div className="space-y-4"><Skeleton className="h-16 w-80" /><Skeleton className="h-96" /></div>}
      </Page>
    );
  }

  const planned = data.entries.filter((e) => e.movable);
  // Nach Stunden gruppieren; Einträge ohne Stunde (von Hand „als Nächstes“) gehören zur laufenden.
  const groups: Array<{ label: string; entries: PlanEntry[] }> = [];
  for (const e of data.entries) {
    const label = e.status !== "planned" ? "Jetzt" : e.plan_hour ? `${new Date(e.plan_hour).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr` : "Jetzt";
    const g = groups[groups.length - 1];
    if (g && g.label === label) g.entries.push(e);
    else groups.push({ label, entries: [e] });
  }

  /** Eintrag davor in der geplanten Reihenfolge (für „nach oben“). */
  const previousPlannedId = (e: PlanEntry, steps: number): string | null | undefined => {
    const idx = planned.findIndex((p) => p.id === e.id);
    const target = idx - steps;
    if (target < 0) return null;
    return planned[target]?.id;
  };

  const jetzt = data.entries.find((e) => e.status === "playing");

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow={<Link to={`/channels/${slug}`} className="hover:text-brand-primary">Kanal · {slug}</Link>}
        title="Sendeablauf"
        description={
          data.default_clock
            ? <>Geplant nach der Stundenuhr „{data.default_clock.name}“ (oder der Uhr im Sendeplan). Von Hand geänderte Einträge bleiben beim Neuplanen erhalten. Zur vollen Stunde beginnt die neue Stunde, auch wenn von der alten noch etwas offen ist.</>
            : <>Ohne Stundenuhr wählt die Automatik jeden Titel kurz vor dem Ende des laufenden. Von Hand eingefügte Titel kommen zuerst. <Link to="/clocks" className="font-medium text-brand-primary hover:underline">Stundenuhr anlegen</Link></>
        }
        actions={
          <>
            {isAdmin && data.default_clock && (
              <button disabled={busy} onClick={() => window.confirm("Die kommenden Stunden neu nach der Uhr planen? Von Hand eingefügte und verschobene Einträge bleiben.") && void act(() => api.replan(slug), "Neu geplant.")}
                className="btn btn-secondary"><Icon name="refresh" /> Neu planen</button>
            )}
            {canEdit && <button onClick={() => setInsertAfter(null)} className="btn btn-primary"><Icon name="plus" /> Titel als Nächstes</button>}
          </>
        }
      />
      <div className="space-y-4">
      {error && <Callout tone="danger">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}

      {/* Jetzt läuft – groß, damit man es aus dem Augenwinkel sieht */}
      {jetzt && (
        <section className="card brand-glow flex min-w-0 items-center gap-4 p-5">
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-primary text-white shadow-glow">
            <EqBars className="h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="section-label !text-brand-primary">Jetzt läuft</div>
            <div className="mt-0.5 truncate text-lg font-semibold text-primary">{[jetzt.artist, jetzt.title].filter(Boolean).join(" – ")}</div>
          </div>
          <div className="hidden text-right sm:block">
            <div className="text-xs text-muted">seit {time(jetzt.eta)}</div>
            {jetzt.air_ms ? <div className="font-mono text-sm tabular-nums text-secondary">{mmss(jetzt.air_ms)}</div> : null}
          </div>
        </section>
      )}

      <RequestInbox slug={slug} compact />
      <MessageInbox slug={slug} canModerate={canModerate} compact />
      {insertAfter !== undefined && (
        <InsertTrack
          onPick={(item) => void act(() => api.insertPlanEntry(slug, item.id, insertAfter), `„${item.title}“ eingefügt.`).then(() => setInsertAfter(undefined))}
          onClose={() => setInsertAfter(undefined)}
        />
      )}

      {data.entries.length === 0 ? (
        <EmptyState icon={<Icon name="music" />} title="Nichts geplant." description="Die Automatik wählt Titel direkt aus Playlist oder Mediathek." />
      ) : (
        groups.map((g, gi) => (
          <section key={`${g.label}-${gi}`} className="card overflow-hidden">
            <div className="flex items-center justify-between border-b border-default bg-[var(--bg-sunken)] px-4 py-2">
              <span className={`section-label ${g.label === "Jetzt" ? "!text-brand-primary" : ""}`}>{g.label}</span>
              <span className="text-xs tabular-nums text-muted">{g.entries.length} {g.entries.length === 1 ? "Eintrag" : "Einträge"}</span>
            </div>
            <ul className="divide-y divide-[var(--border)]">
              {g.entries.map((e) => {
                const style = KIND_STYLE[e.kind] ?? { label: e.kind, className: "bg-[var(--text-muted)]" };
                const playing = e.status === "playing";
                return (
                  <li key={e.id} className={`group flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 ${playing ? "bg-brand-primary/[0.06] shadow-[inset_3px_0_0_rgb(var(--brand-primary-rgb))]" : ""} ${e.skipped_at_hour ? "opacity-60" : ""}`}>
                    <span className={`w-16 font-mono text-xs tabular-nums ${playing ? "font-semibold text-brand-primary" : "text-muted"}`}>{time(e.eta)}</span>
                    <span className="inline-flex w-28 items-center gap-1.5 truncate text-xs font-medium text-secondary">
                      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${style.className}`} aria-hidden="true" />
                      <span className="truncate">{e.slot ?? style.label}</span>
                    </span>
                    <span className={`flex min-w-0 flex-1 basis-40 items-center gap-2 text-sm ${playing ? "font-semibold text-primary" : "text-secondary"}`}>
                      {playing && <EqBars className="flex-shrink-0 text-brand-primary" />}
                      <span className="truncate">{[e.artist, e.title].filter(Boolean).join(" – ")}</span>
                      {e.locked && <span className="flex-shrink-0 text-[11px] text-muted" title="Von Hand eingefügt oder verschoben – bleibt beim Neuplanen">✎</span>}
                      {e.skipped_at_hour && <span className="badge badge-neutral flex-shrink-0" title="Die Stunde ist voll: Zur vollen Stunde beginnt die nächste, dieser Eintrag entfällt.">entfällt</span>}
                    </span>
                    <span className="w-12 text-right font-mono text-xs tabular-nums text-muted">{mmss(e.air_ms)}</span>
                    {canEdit && e.movable && (
                      <span className="flex items-center gap-0.5 opacity-70 transition-opacity group-hover:opacity-100">
                        <button disabled={busy} onClick={() => { const after = previousPlannedId(e, 2); if (after !== undefined) void act(() => api.movePlanEntry(e.id, after)); }}
                          className={ICON_BTN} title="nach oben" aria-label="nach oben"><Icon name="up" className="h-3.5 w-3.5" /></button>
                        <button disabled={busy} onClick={() => { const idx = planned.findIndex((p) => p.id === e.id); const next = planned[idx + 1]; if (next) void act(() => api.movePlanEntry(e.id, next.id)); }}
                          className={ICON_BTN} title="nach unten" aria-label="nach unten"><Icon name="down" className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setInsertAfter(e.id)} className={ICON_BTN} title="Titel dahinter einfügen" aria-label="Titel dahinter einfügen"><Icon name="plus" className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setTextFor(textFor === e.id ? null : e.id)}
                          className={`btn btn-sm !px-2 ${e.text ? "bg-brand-primary/10 text-brand-primary" : "btn-ghost"}`}
                          title={e.text ? "Moderationstext ändern" : "Moderationstext schreiben"}>✎ Text</button>
                        <VoicetrackButton slug={slug} afterId={e.id} onDone={(msg) => void act(async () => undefined, msg)} onError={setError} />
                        <button disabled={busy} onClick={() => void act(() => api.removePlanEntry(e.id))} className={`${ICON_BTN} text-red-600 dark:text-red-400`} title="herausnehmen" aria-label="herausnehmen"><Icon name="x" className="h-3.5 w-3.5" /></button>
                      </span>
                    )}
                    {textFor === e.id ? (
                      <ManuskriptEditor
                        entry={e}
                        disabled={busy}
                        onSave={(text) => void act(() => api.setPlanText(e.id, text), text.trim() ? "Text gesichert." : "Text gelöscht.").then(() => setTextFor(null))}
                        onClose={() => setTextFor(null)}
                      />
                    ) : (
                      e.text && (
                        <button onClick={() => canEdit && setTextFor(e.id)} className="w-full basis-full cursor-text rounded-lg bg-input px-3 py-2 text-left text-xs leading-relaxed text-secondary ring-1 ring-[var(--border)] hover:ring-[var(--border-strong)]">
                          <span className="whitespace-pre-line line-clamp-3">{e.text}</span>
                        </button>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
      <div className="well space-y-1.5 text-xs leading-relaxed text-muted">
        <p>
          Moderationen lassen sich hier als fertige Datei an eine Stelle setzen. Mit Vorhören von Aus- und Einleitung aufnehmen geht im Studio.
        </p>
        <p>
          „✎ Text“ schreibt das Manuskript an einen Eintrag: Es steht im Studio im Fach <em>Manuskript</em> groß zum Vorlesen – der laufende Text oben,
          der nächste darunter. Wer moderiert, darf ihn dort auch noch ändern.
        </p>
      </div>
      </div>
    </Page>
  );
}

/** Das Manuskript an einem Eintrag: schreiben, sichern, verwerfen.
 *
 * Bewusst ein Textfeld ohne Formatierung: Was vorgelesen wird, braucht keine
 * Fettschrift, sondern Absätze – und die überleben jeden Weg zum Studio.
 */
function ManuskriptEditor({ entry, disabled, onSave, onClose }: { entry: PlanEntry; disabled: boolean; onSave: (text: string) => void; onClose: () => void }): JSX.Element {
  const [text, setText] = useState(entry.text ?? "");
  const geaendert = text !== (entry.text ?? "");
  return (
    <div className="basis-full space-y-2 py-2">
      <textarea
        autoFocus
        value={text}
        onChange={(ev) => setText(ev.target.value)}
        rows={6}
        maxLength={20000}
        placeholder="Was hier steht, liest die Moderation vor. Absätze mit einer Leerzeile trennen."
        className="input font-sans leading-relaxed"
      />
      <div className="flex flex-wrap items-center gap-2">
        <button disabled={disabled || !geaendert} onClick={() => onSave(text)} className="btn btn-primary btn-sm">Sichern</button>
        <button onClick={onClose} className="btn btn-ghost btn-sm">Schließen</button>
        {entry.text && !text.trim() && <span className="text-xs text-muted">Leer sichern löscht den Text.</span>}
        <span className="ml-auto text-xs tabular-nums text-muted">
          {text.trim() ? `${text.trim().split(/\s+/).length} Wörter · etwa ${Math.max(1, Math.round(text.trim().split(/\s+/).length / 130 * 60))} Sekunden` : ""}
          {entry.text_at && !geaendert ? ` · zuletzt ${new Date(entry.text_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}` : ""}
        </span>
      </div>
    </div>
  );
}

function VoicetrackButton({ slug, afterId, onDone, onError }: { slug: string; afterId: string; onDone: (msg: string) => void; onError: (msg: string) => void }): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button onClick={() => ref.current?.click()} className="btn btn-ghost btn-sm !w-7 !px-0" title="Moderation (Datei) dahinter setzen" aria-label="Moderation (Datei) dahinter setzen">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true">
          <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
        </svg>
      </button>
      <input ref={ref} type="file" accept="audio/*" hidden onChange={(ev) => {
        const file = ev.target.files?.[0];
        ev.target.value = "";
        if (!file) return;
        api.uploadVoicetrack(slug, file, afterId, file.name.replace(/\.[^.]+$/, "")).then(() => onDone("Moderation eingesetzt. Sie wird gerade gemessen.")).catch((err) => onError(errorText(err)));
      }} />
    </>
  );
}

function InsertTrack({ onPick, onClose }: { onPick: (item: LibraryItem) => void; onClose: () => void }): JSX.Element {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<LibraryItem[]>([]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.listLibrary({ q: q || undefined, limit: 30 }).then(setItems).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="card animate-fade-in space-y-3 p-4 shadow-md">
      <div className="flex gap-2">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titel oder Interpret suchen" className="input flex-1" />
        <button onClick={onClose} className="btn btn-ghost">Schließen</button>
      </div>
      <ul className="max-h-64 divide-y divide-[var(--border)] overflow-auto rounded-lg border border-default">
        {items.filter((i) => i.analysis_status !== "failed").map((i) => (
          <li key={i.id}>
            <button onClick={() => onPick(i)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--bg-hover)]">
              <span className="min-w-0 flex-1 truncate text-primary">{[i.artist, i.title].filter(Boolean).join(" – ")}</span>
              <span className="font-mono text-xs tabular-nums text-muted">{mmss(i.duration_ms)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
